import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SignupInitialModel, SignupModel, SignupPasswordModel, UserProviderValue } from '../models';
import { SignupInitialSchema, SignupPasswordSchema } from '../schemas';
import { SignupStatuses } from '../enums';
import { useContext, useEffect, useMemo, useState } from 'react';
import { requestEmailVerification, signup, signupCheck } from '../services';
import { toast } from 'react-toastify';
import { UserContext } from '../contexts/user.context';
import { useSearchParams } from 'react-router-dom';
import { getSecurityMathQuestion } from '../utils';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';

(window as any).dataLayer = (window as any).dataLayer || [];

export function useSignupWeb2() {
   const { setUser, setIsAuthenticated, setIsLoginModalOpen, logOut } = useContext<UserProviderValue>(UserContext);
   const [searchParams] = useSearchParams();
   const referralCode = searchParams.get('referral');
   const firstname = searchParams.get('firstname');
   const lastname = searchParams.get('lastname');
   const email = searchParams.get('email');
   const source = searchParams.get('source'); // sweepstakes, referral, etc.
   const utmSource = searchParams.get('utm_source');
   const utmCampaign = searchParams.get('utm_campaign');
   const utmContent = searchParams.get('utm_content');
   const utmMedium = searchParams.get('utm_medium');

   const [securityMathInput, setSecurityMathInput] = useState('');
   const securityMathQuestion = useMemo(() => getSecurityMathQuestion(), []);
   const [securityMathPassed, setSecurityMathPassed] = useState(false);

   const [signupStatus, setSignupStatus] = useState<SignupStatuses>(SignupStatuses.Initial);
   const [stepInfo, setStepInfo] = useState<{
      step: number;
      icon: string;
      title: string;
      subtitle: string;
   }>({
      step: 1,
      icon: '/assets/images/ticket-bg-2.png',
      title: "You're One Step Closer to Ireland",
      subtitle: 'Finish setting up your account to see your real-time odds, track your progress, and get additional entries.',
   });

   const [signupData, setSignupData] = useState<SignupModel | null>(null);
   const [isLoading, setIsLoading] = useState(false);

   const signupCheckForm = useForm<SignupInitialModel>({
      resolver: zodResolver(SignupInitialSchema),
      mode: 'onChange',
      defaultValues: {
         referralCode: referralCode || '',
         firstname: firstname || '',
         lastname: lastname || '',
         email: email || '',
         source: source || '',
         utmSource: utmSource || '',
         utmCampaign: utmCampaign || '',
         utmContent: utmContent || '',
         utmMedium: utmMedium || '',
      },
   });
   const signupPasswordForm = useForm<SignupPasswordModel>({
      resolver: zodResolver(SignupPasswordSchema),
      mode: 'onChange',
      defaultValues: {
         password: '',
         confirmPassword: '',
      },
   });

   const getStepInfo = () => {
      switch (signupStatus) {
         default:
         case SignupStatuses.Initial:
            logOut();
            return {
               step: 1,
               icon: '/assets/images/ticket-bg-2.png',
               title: "You're One Step Closer to Ireland",
               subtitle: 'Finish setting up your account to see your real-time odds, track your progress, and get additional entries.',
            };
         case SignupStatuses.InformationVerified:
            return {
               step: 2,
               icon: '/assets/icons/lock-icon.png',
               title: 'Set Your Password',
               subtitle: 'Secure Your Account. Set a password to protect your rewards.',
            };
         case SignupStatuses.VerificationEmailSent:
            return {
               step: 3,
               icon: '/assets/icons/email-icon.png',
               title: 'By Email',
               subtitle: '',
            };
      }
   };

   useEffect(() => {
      setStepInfo(getStepInfo());
   }, [signupStatus]);

   useEffect(() => {
      if (signupCheckForm.watch('country') === 'Canada') {
         if (Number(securityMathInput) === securityMathQuestion.result) {
            setSecurityMathPassed(true);
         } else {
            setSecurityMathPassed(false);
         }
      } else {
         setSecurityMathPassed(true);
      }
   }, [signupCheckForm.watch('country'), securityMathInput]);

   const submitCheck = async (data: SignupInitialModel) => {
      if (!signupCheckForm.formState.isValid) return;
      setIsLoading(true);
      const checkResponse = await signupCheck(data);

      if (checkResponse.error) {
         toast.error(checkResponse.error.message || 'This email or username is already in use');
         setIsLoading(false);
         return;
      }

      setSignupData({
         email: data.email,
         firstname: data.firstname,
         lastname: data.lastname,
         referralCode: data.referralCode,
         termsAccepted: data.termsAccepted,
         country: data.country,
         source: data.source,
         utmSource: data.utmSource,
         utmCampaign: data.utmCampaign,
         utmContent: data.utmContent,
         utmMedium: data.utmMedium,
      } as SignupModel);

      setSignupStatus(SignupStatuses.InformationVerified);
      // (window as any).dataLayer.push({
      //    event: 'sign_up_started',
      //    category: 'Account Creation',
      //    action: 'Sign Up Form Started (1/3)',
      //    label: 'User started the sign-up form',
      // });
      try {
         ReactGA.event({
            category: 'Account Creation',
            action: 'Sign Up Form Started (1/3)',
            label: 'User started the sign up form by entering their information',
         });
         TagManager.dataLayer({
            dataLayer: {
               event: 'signup_form_started',
               category: 'Account Creation',
               action: 'Sign Up Form Started (1/3)',
               label: 'User started the sign-up form',
            },
         });

         ReactGA.send({ hitType: 'pageview', page: '/signup-enter-password' });
      } catch (e) {
         console.error(e);
      }

      setIsLoading(false);
   };

   const submitPassword = async (data: SignupPasswordModel, retry?: boolean) => {
      if (!signupData || !data.password || !data.confirmPassword) {
         toast.error('An error occurred. Please try again');
         setSignupStatus(SignupStatuses.Initial);
         return;
      }
      setIsLoading(true);
      const requestEmailVerificationResponse = await requestEmailVerification(signupData.email);
      if (requestEmailVerificationResponse.error) {
         toast.error(requestEmailVerificationResponse.error.message || 'Failed to send verification email');
         setIsLoading(false);
         return;
      }

      setSignupData({ ...signupData, password: data.password, confirmPassword: data.confirmPassword });
      setSignupStatus(SignupStatuses.VerificationEmailSent);

      if (!retry) {
         try {
            ReactGA.event({
               category: 'Account Creation',
               action: 'Sign Up Form Completed (2/3)',
               label: 'User completed the sign up form by setting their password',
            });
            // (window as any).dataLayer.push({
            //    event: 'signup_form_completed',
            //    category: 'Account Creation',
            //    action: 'Sign Up Form Completed',
            //    label: 'User completed the sign-up form by setting their password',
            // });
            TagManager.dataLayer({
               dataLayer: {
                  event: 'signup_form_completed',
                  category: 'Account Creation',
                  action: 'Sign Up Form Completed',
                  label: 'User completed the sign-up form by setting their password',
               },
            });
            ReactGA.send({ hitType: 'pageview', page: '/signup-enter-email-verification' });
         } catch (e) {
            console.error(e);
         }
      }

      setIsLoading(false);
   };

   const submitSignup = async (validationCode: string) => {
      if (!signupData) {
         toast.error('An error occurred. Please try again');
         setSignupStatus(SignupStatuses.Initial);
         return;
      }
      if (!validationCode) {
         toast.error('Please enter the validation code');
         return;
      }

      setIsLoading(true);

      const signupResponse = await signup({ ...signupData, validationCode, newUser: true });
      if (signupResponse.error || !signupResponse.data) {
         toast.error(signupResponse.error?.message || 'Failed to create account');
         setIsLoading(false);
         return;
      }

      try {
         ReactGA.event({
            category: 'Account Creation',
            action: 'Account Verified (3/3)',
            label: 'User successfully verified their account',
         });

         TagManager.dataLayer({
            dataLayer: {
               event: 'account_verified',
               category: 'Account Creation',
               action: 'Account Verified',
               label: 'User successfully verified their account',
            },
         });

         ReactGA.send({ hitType: 'pageview', page: '/signup-verified' });
      } catch (e) {
         console.error(e);
      }

      setUser(signupResponse.data);
      setIsAuthenticated(true);
      toast.success('Account created successfully');
      setIsLoading(false);
   };

   return {
      signupCheckForm,
      submitCheck,
      signupStatus,
      isLoading,
      submitSignup,
      setSignupStatus,
      securityMathInput,
      setSecurityMathInput,
      securityMathPassed,
      securityMathQuestion,
      setIsLoginModalOpen,
      submitPassword,
      signupPasswordForm,
      stepInfo,
   };
}
